/**
 * Storage service layer.
 * Abstracts all Firebase Storage operations from the rest of the application.
 * Handles image upload, deletion, validation, and compression.
 */
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGE_WIDTH = 1200;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate an image file for type and size constraints.
 * @param {File} file - The file to validate.
 * @throws {Error} If the file fails validation.
 */
const validateImageFile = (file) => {
  if (!file) {
    throw new Error('No file provided.');
  }
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File is too large. Maximum size is 5MB.');
  }
};

/**
 * Compress and resize an image using a Canvas-based approach.
 * @param {File} file - The original image file.
 * @param {number} [maxWidth=1200] - The maximum width in pixels.
 * @param {number} [quality=0.8] - The JPEG compression quality (0-1).
 * @returns {Promise<Blob>} A compressed image blob.
 */
const compressImage = (file, maxWidth = MAX_IMAGE_WIDTH, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    // GIFs should not be compressed (would lose animation)
    if (file.type === 'image/gif') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Only resize if the image is wider than maxWidth
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image.'));
            }
          },
          outputType,
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image for compression.'));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
};

/**
 * Upload a post image to Firebase Storage with compression and progress tracking.
 * @param {File} file - The image file to upload.
 * @param {string} postId - The post ID for the storage path.
 * @param {function} [onProgress] - Optional callback receiving upload progress (0-100).
 * @returns {Promise<string>} The download URL of the uploaded image.
 */
export const uploadPostImage = async (file, postId, onProgress) => {
  validateImageFile(file);

  const compressedBlob = await compressImage(file);
  const extension = file.name.split('.').pop() || 'jpg';
  const storageRef = ref(storage, `posts/${postId}/image.${extension}`);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
      contentType: compressedBlob.type || file.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        if (onProgress) onProgress(progress);
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[Upload post image]', error);
        }
        reject(new Error('Failed to upload image. Please try again.'));
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[Get download URL]', error);
          }
          reject(new Error('Failed to get image URL after upload.'));
        }
      }
    );
  });
};

/**
 * Delete a post image from Firebase Storage by its download URL.
 * @param {string} imageUrl - The full download URL of the image.
 * @returns {Promise<void>}
 */
export const deletePostImage = async (imageUrl) => {
  if (!imageUrl) return;

  try {
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
  } catch (error) {
    // If the image doesn't exist, that's fine - it may have already been deleted
    if (error.code === 'storage/object-not-found') {
      return;
    }
    handleServiceError(error, 'Delete post image', 'Failed to delete image. Please try again.');
  }
};

/**
 * Upload an avatar image to Firebase Storage with compression.
 * @param {File} file - The avatar image file.
 * @param {string} userId - The user ID for the storage path.
 * @param {function} [onProgress] - Optional callback receiving upload progress (0-100).
 * @returns {Promise<string>} The download URL of the uploaded avatar.
 */
/**
 * Upload a cover photo to Firebase Storage with compression.
 * @param {File} file - The cover photo image file.
 * @param {string} userId - The user ID for the storage path.
 * @param {function} [onProgress] - Optional callback receiving upload progress (0-100).
 * @returns {Promise<string>} The download URL of the uploaded cover photo.
 */
export const uploadCoverPhoto = async (file, userId, onProgress) => {
  validateImageFile(file);

  const compressedBlob = await compressImage(file, 1200, 0.85);
  const extension = file.name.split('.').pop() || 'jpg';
  const storageRef = ref(storage, `covers/${userId}.${extension}`);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
      contentType: compressedBlob.type || file.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        if (onProgress) onProgress(progress);
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[Upload cover photo]', error);
        }
        reject(new Error('Failed to upload cover photo. Please try again.'));
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[Get cover photo URL]', error);
          }
          reject(new Error('Failed to get cover photo URL after upload.'));
        }
      }
    );
  });
};

/**
 * Upload an avatar image to Firebase Storage with compression.
 * @param {File} file - The avatar image file.
 * @param {string} userId - The user ID for the storage path.
 * @param {function} [onProgress] - Optional callback receiving upload progress (0-100).
 * @returns {Promise<string>} The download URL of the uploaded avatar.
 */
export const uploadAvatar = async (file, userId, onProgress) => {
  validateImageFile(file);

  const compressedBlob = await compressImage(file, 400, 0.85);
  const extension = file.name.split('.').pop() || 'jpg';
  const storageRef = ref(storage, `avatars/${userId}.${extension}`);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, compressedBlob, {
      contentType: compressedBlob.type || file.type,
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        if (onProgress) onProgress(progress);
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('[Upload avatar]', error);
        }
        reject(new Error('Failed to upload avatar. Please try again.'));
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadUrl);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[Get avatar URL]', error);
          }
          reject(new Error('Failed to get avatar URL after upload.'));
        }
      }
    );
  });
};
